// Shared "Version history" drawer for the artifact preview panel. Right-docked, confined to the
// panel's own bounds (absolute, not a portaled MUI Drawer) so it matches the mock's layout inside
// both ArtifactPreviewPanel (library detail view) and ChatWorkspace's live markdown panel.
//
// Two render modes because only markdown is wired to the shared artifact-lifecycle engine today:
// - 'markdown': full revision cards with View/Compare/Restore + a collapsible provenance Details row.
// - 'flat': website/survey's existing published-version list, View/Download only, no compare/restore/
//   provenance (that data doesn't exist for these types yet).
import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import RestoreIcon from '@mui/icons-material/Restore'
import type { MarkdownArtifactRevisionSummary } from '@reachinsight-app/shared/src/components/R3Chat/types'
import type { PanelArtifactVersion } from './rosArtifacts'
import { relativeModified } from '../pages/artifactsModel'

function originLabel(origin: MarkdownArtifactRevisionSummary['origin']): string {
  switch (origin) {
    case 'initial-generation': return 'Created'
    case 'model-tool': return 'Generated by Ros'
    case 'skill-tool': return 'Generated by a skill'
    case 'user-edit': return 'Edited'
    case 'user-undo': return 'Undone'
    case 'user-restore': return 'Restored'
    case 'import': return 'Imported'
    case 'migration': return 'Migrated'
    case 'system-repair': return 'System repair'
    default: return 'Generated by Ros'
  }
}

const drawerSx = {
  position: 'absolute' as const, top: 0, bottom: 0, right: 0, width: 340, maxWidth: '88%',
  bgcolor: 'background.paper', borderLeft: '1px solid', borderColor: 'divider',
  display: 'flex', flexDirection: 'column', zIndex: 10, boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
}

interface MarkdownDrawerProps {
  kind: 'markdown'
  revisions: MarkdownArtifactRevisionSummary[]
  currentRevision: number
  busy: string | null
  onClose: () => void
  onView: (revision: number) => void
  onCompare: (revision: number) => void
  onRestore: (revision: number) => void
}

interface FlatDrawerProps {
  kind: 'flat'
  versions: PanelArtifactVersion[]
  currentVersion: number
  onClose: () => void
  onView: (version: PanelArtifactVersion) => void
  onDownload: (version: PanelArtifactVersion) => void
}

export type ArtifactVersionsDrawerProps = MarkdownDrawerProps | FlatDrawerProps

const cardSx = { border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mb: 1.5, overflow: 'hidden' }
const cardActionsSx = { display: 'flex', gap: 1, px: 1.75, pb: 1.5 }
const cardActionButtonSx = { fontSize: '0.72rem', fontWeight: 600 }

export function ArtifactVersionsDrawer(props: ArtifactVersionsDrawerProps) {
  return (
    <Box sx={drawerSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '1.05rem' }}>Version history</Typography>
        <IconButton size="small" onClick={props.onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {props.kind === 'markdown'
          ? [...props.revisions].sort((a, b) => b.revision - a.revision).map(revision => (
              <MarkdownRevisionCard
                key={revision.revision}
                revision={revision}
                isCurrent={revision.revision === props.currentRevision}
                busy={props.busy}
                onView={() => props.onView(revision.revision)}
                onCompare={() => props.onCompare(revision.revision)}
                onRestore={() => props.onRestore(revision.revision)}
              />
            ))
          : [...props.versions].sort((a, b) => b.version - a.version).map(version => (
              <FlatVersionRow
                key={version.version}
                version={version}
                isCurrent={version.version === props.currentVersion}
                onView={() => props.onView(version)}
                onDownload={() => props.onDownload(version)}
              />
            ))}
      </Box>
    </Box>
  )
}

function MarkdownRevisionCard({ revision, isCurrent, busy, onView, onCompare, onRestore }: {
  revision: MarkdownArtifactRevisionSummary
  isCurrent: boolean
  busy: string | null
  onView: () => void
  onCompare: () => void
  onRestore: () => void
}) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  return (
    <Box sx={cardSx}>
      <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Version {revision.revision + 1}</Typography>
          {isCurrent && <Chip label="Current" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{relativeModified(revision.createdAt)}</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.5 }}>{revision.summary}</Typography>
      </Box>
      <Box sx={cardActionsSx}>
        <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon fontSize="small" />} disabled={busy !== null} onClick={onView} sx={cardActionButtonSx}>View</Button>
        {!isCurrent && <Button size="small" variant="outlined" startIcon={<CompareArrowsIcon fontSize="small" />} disabled={busy !== null} onClick={onCompare} sx={cardActionButtonSx}>Compare</Button>}
        {!isCurrent && <Button size="small" variant="outlined" color="primary" startIcon={<RestoreIcon fontSize="small" />} disabled={busy !== null} onClick={onRestore} sx={cardActionButtonSx}>Restore</Button>}
      </Box>
      <Box
        onClick={() => setDetailsOpen(open => !open)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.75, py: 1, borderTop: '1px solid', borderColor: 'divider', cursor: 'pointer', bgcolor: 'action.hover' }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', fontSize: '0.65rem' }}>Details</Typography>
        <Box sx={{ flex: 1 }} />
        {detailsOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
      </Box>
      {detailsOpen && (
        <Box sx={{ px: 1.75, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}>{originLabel(revision.origin)}</Typography>
          {revision.models.map(model => (
            <Typography key={model.attemptId} variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}>
              {model.provider}/{model.model}{model.role ? ` · ${model.role}` : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  )
}

function FlatVersionRow({ version, isCurrent, onView, onDownload }: {
  version: PanelArtifactVersion
  isCurrent: boolean
  onView: () => void
  onDownload: () => void
}) {
  return (
    <Box sx={cardSx}>
      <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Version {version.version}</Typography>
          {isCurrent && <Chip label="Current" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{relativeModified(version.publishedAt)}</Typography>
        </Box>
      </Box>
      <Box sx={cardActionsSx}>
        <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon fontSize="small" />} onClick={onView} sx={cardActionButtonSx}>View</Button>
        <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon fontSize="small" />} onClick={onDownload} sx={cardActionButtonSx}>Download</Button>
      </Box>
    </Box>
  )
}
